import { FieldKey } from '../../context/field-keys.js';
import { roundValue } from '../../shared/round.js';
/** Legacy `SpaceAutoCalculationHelper.parseNumber`: formatted-string tolerant, 0 on empty/NaN. */
export function parseNumber(value) {
  if (value == null || value === '') {
    return 0;
  }
  if (typeof value === 'number') {
    return value;
  }
  const parsed = parseFloat(String(value).replace(/[^0-9.-]+/g, ''));
  return isNaN(parsed) ? 0 : parsed;
}
/** amount = round2(rate × size); null when size ≤ 0, rate ≤ 0, or the product is not finite. */
export function amountFromRateRule(pair) {
  return {
    id: `${pair.idPrefix}.amount-from-rate`,
    inputs: [pair.rate, pair.size],
    outputs: [pair.amount],
    formulaVersion: '1.0.0',
    run: (ctx, deps) => {
      // INPUT-side null-on-≤0 guards (legacy engine semantics on the operands, applied before
      // any computation) — roundValue's output-side `nonPositive: 'null'` cannot express these,
      // so they stay as guards (T2.8).
      const size = parseNumber(ctx.get(pair.size));
      if (!size || size <= 0) {
        // Legacy engine clears both sides when size is missing/non-positive; the rule clears its output.
        ctx.set(pair.amount, null);
        return;
      }
      const rate = parseNumber(ctx.get(pair.rate));
      if (!(rate > 0)) {
        ctx.set(pair.amount, null);
        return;
      }
      const raw = rate * size;
      // OUTPUT-side null-on-≤0 (pinned roundToTwoDecimals semantics): the default path IS
      // roundValue's `nonPositive: 'null'` mode; an injected host round keeps the identical
      // finite-and-positive gate around it.
      if (deps?.round) {
        ctx.set(pair.amount, Number.isFinite(raw) && raw > 0 ? deps.round(raw, pair.amount) : null);
      } else {
        ctx.set(pair.amount, roundValue(raw, {
          nonPositive: 'null'
        }));
      }
    }
  };
}
/** rate = round2(amount ÷ size); null when size ≤ 0, amount ≤ 0, or the quotient is not finite. */
export function rateFromAmountRule(pair) {
  return {
    id: `${pair.idPrefix}.rate-from-amount`,
    inputs: [pair.amount, pair.size],
    outputs: [pair.rate],
    formulaVersion: '1.0.0',
    run: (ctx, deps) => {
      // INPUT-side null-on-≤0 guards — see amountFromRateRule; roundValue cannot express these.
      const size = parseNumber(ctx.get(pair.size));
      if (!size || size <= 0) {
        ctx.set(pair.rate, null);
        return;
      }
      const amount = parseNumber(ctx.get(pair.amount));
      if (!(amount > 0)) {
        ctx.set(pair.rate, null);
        return;
      }
      const raw = amount / size;
      // OUTPUT-side null-on-≤0: default path = roundValue nonPositive:'null'; injected host
      // round keeps the identical finite-and-positive gate (see amountFromRateRule).
      if (deps?.round) {
        ctx.set(pair.rate, Number.isFinite(raw) && raw > 0 ? deps.round(raw, pair.rate) : null);
      } else {
        ctx.set(pair.rate, roundValue(raw, {
          nonPositive: 'null'
        }));
      }
    }
  };
}
/** Build both directions for one pair. */
export function sizePairRules(pair) {
  return [amountFromRateRule(pair), rateFromAmountRule(pair)];
}
/** 1. Management Fee Per Month ⇄ Management Fee Per Sq Ft. */
export const HK_MGMT_FEE_PAIR = {
  idPrefix: 'space.hk-retail.mgmt-fee',
  amount: FieldKey.SpaceMgmtFeePerMonth,
  rate: FieldKey.SpaceMgmtFeePerSqFt,
  size: FieldKey.SpaceAvailableSpace
};
/** 2. Total Monthly Asking Rent ⇄ Quoted Unit Rent. */
export const HK_ASKING_RENT_PAIR = {
  idPrefix: 'space.hk-retail.asking-rent',
  amount: FieldKey.SpaceTotalMonthlyAskingRent,
  rate: FieldKey.SpaceQuotedUnitRent,
  size: FieldKey.SpaceAvailableSpace
};
/** 3. Sale Price ⇄ Sale Price by Sq Ft. */
export const HK_SALE_PRICE_PAIR = {
  idPrefix: 'space.hk-retail.sale-price',
  amount: FieldKey.SpaceSalePrice,
  rate: FieldKey.SpaceSalePricePerSqFt,
  size: FieldKey.SpaceAvailableSpace
};
/** 4. A/C Normal Charges ⇄ A/C Charges Per Sq Ft (FormArray entry in the host). */
export const HK_AC_CHARGES_PAIR = {
  idPrefix: 'space.hk-retail.ac-charges',
  amount: FieldKey.SpaceAcNormalCharges,
  rate: FieldKey.SpaceAcChargesPerSqFt,
  size: FieldKey.SpaceAvailableSpace
};
/** 5. Rental ⇄ Rental Per Sq Ft (FormArray entry; same math as `space-rental-auto-calculation.ts`). */
export const HK_RENTAL_PAIR = {
  idPrefix: 'space.hk-retail.rental',
  amount: FieldKey.SpaceRentalMonthly,
  rate: FieldKey.SpaceRentalPerSqFt,
  size: FieldKey.SpaceAvailableSpace
};
/** All five HK-Retail pairs, both directions each (10 rules). */
export const hkRetailSizePairRules = [...sizePairRules(HK_MGMT_FEE_PAIR), ...sizePairRules(HK_ASKING_RENT_PAIR), ...sizePairRules(HK_SALE_PRICE_PAIR), ...sizePairRules(HK_AC_CHARGES_PAIR), ...sizePairRules(HK_RENTAL_PAIR)];
